import {
  fulfillmentMethodValues,
  onboardingDraftVersion,
  paymentMethodValues,
  weekdayValues,
  type AttributionDraft,
  type FulfillmentMethod,
  type OnboardingDraft,
  type PaymentMethod,
  type ServiceDraft,
  type ServiceScheduleDraft,
  type StoreDraft,
  type Weekday,
} from '@marketside/shared-contracts';
import { z } from 'zod';

/**
 * Operational onboarding draft.
 *
 * It is complete-shaped so every input can stay controlled, but it only contains data needed to
 * activate and run the store: attribution, exact store location, opening hours, payments and
 * delivery preferences.
 */
export const draftVersion = onboardingDraftVersion;

export type PaymentMethodDraft = PaymentMethod;
export type WeekdayDraft = Weekday;
export const fulfillmentMethods = fulfillmentMethodValues;
export type FulfillmentMethodDraft = FulfillmentMethod;
export type AttributionDraftShape = AttributionDraft;
export type StoreDraftShape = StoreDraft;
export type ScheduleDraft = ServiceScheduleDraft;
export type ServiceDraftShape = ServiceDraft;
export type { OnboardingDraft };

/** Sections the merchant fills in and that have completeness rules. */
export type DraftSection = 'store' | 'service';

/** Everything patchable, including `attribution`, which is captured from the URL rather than typed. */
export type PatchableSection = DraftSection | 'attribution';

export const weekdays: WeekdayDraft[] = [...weekdayValues];

export function emptyDraft(): OnboardingDraft {
  return {
    version: draftVersion,
    updatedAt: '',
    attribution: { referenceCode: '', platform: '', clickId: '', clickIdType: '' },
    store: {
      name: '',
      googlePlaceId: '',
      formattedAddress: '',
      latitude: '',
      longitude: '',
    },
    service: {
      // Seeded open weekdays so the most tedious step starts mostly answered.
      schedules: weekdays.map((day) => ({
        day,
        isOpen: day !== 'sun',
        opensAt: '08:00',
        closesAt: '18:00',
      })),
      paymentMethods: [],
      fulfillmentMethods: [],
      deliveryNotes: '',
      deliveryArea: { enabled: false, neighborhoods: [] },
    },
  };
}

// --- validation -------------------------------------------------------------------------------

export function onlyDigits(value: string): string {
  return value.replace(/\D/gu, '');
}

function isTimeBefore(start: string, end: string): boolean {
  if (!start || !end) {
    return false;
  }

  return start < end;
}

function isCoordinate(value: string): boolean {
  if (!value.trim()) {
    return false;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed);
}

function hasEnabledDeliveryAreaConfiguration(value: ServiceDraft): boolean {
  if (!value.deliveryArea.enabled) {
    return true;
  }

  if (value.deliveryArea.radiusKm !== undefined) {
    return true;
  }

  return value.deliveryArea.neighborhoods.length > 0;
}

const storeValidSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da loja.').max(80, 'Use até 80 caracteres.'),
  googlePlaceId: z.string().trim().min(1, 'Escolha o endereço da loja no Google Maps.'),
  formattedAddress: z.string().trim().min(1, 'Escolha o endereço da loja no Google Maps.'),
  latitude: z.string().refine(isCoordinate, 'Escolha um endereço com localização exata.'),
  longitude: z.string().refine(isCoordinate, 'Escolha um endereço com localização exata.'),
});

const serviceValidSchema = z
  .object({
    schedules: z.array(
      z.object({
        day: z.enum(weekdayValues),
        isOpen: z.boolean(),
        opensAt: z.string(),
        closesAt: z.string(),
      }),
    ),
    paymentMethods: z.array(z.enum(paymentMethodValues)).min(1, 'Escolha ao menos uma forma de pagamento.'),
    fulfillmentMethods: z.array(z.enum(fulfillmentMethodValues)).min(1, 'Escolha ao menos uma forma de entrega ou retirada.'),
    deliveryNotes: z.string().max(240, 'Use até 240 caracteres.'),
    deliveryArea: z.object({
      enabled: z.boolean(),
      radiusKm: z.number().positive('Informe um raio maior que zero.').max(100, 'Use até 100 km.').optional(),
      neighborhoods: z.array(z.string().trim().min(1)).max(80, 'Use até 80 bairros.'),
    }),
  })
  .refine((value) => value.schedules.some((schedule) => schedule.isOpen), {
    path: ['schedules'],
    message: 'Marque pelo menos um dia de atendimento.',
  })
  .refine(
    (value) =>
      value.schedules
        .filter((schedule) => schedule.isOpen)
        .every((schedule) => isTimeBefore(schedule.opensAt, schedule.closesAt)),
    {
      path: ['schedules'],
      message: 'O horário de fechamento deve ser depois do de abertura.',
    },
  )
  .refine((value) => hasEnabledDeliveryAreaConfiguration(value), {
    path: ['deliveryArea'],
    message: 'Informe raio máximo ou bairros atendidos, ou desative a validação de área.',
  });

const sectionSchemas = {
  store: storeValidSchema,
  service: serviceValidSchema,
} as const;

export type FieldErrors = Record<string, string>;

/** Field-level errors for one section, keyed by field name. Empty when the section is valid. */
export function sectionErrors(draft: OnboardingDraft, section: DraftSection): FieldErrors {
  const parsed = sectionSchemas[section].safeParse(draft[section]);

  if (parsed.success) {
    return {};
  }

  const errors: FieldErrors = {};

  for (const issue of parsed.error.issues) {
    const key = String(issue.path[0] ?? '');

    if (key && !errors[key]) {
      errors[key] = issue.message;
    }
  }

  return errors;
}

export function isSectionComplete(draft: OnboardingDraft, section: DraftSection): boolean {
  return sectionSchemas[section].safeParse(draft[section]).success;
}

// --- persistence boundary ---------------------------------------------------------------------

const draftShapeSchema = z.object({
  version: z.number().optional(),
  updatedAt: z.string().optional(),
  attribution: z.record(z.string(), z.unknown()).optional(),
  store: z.record(z.string(), z.unknown()).optional(),
  service: z.record(z.string(), z.unknown()).optional(),
}).passthrough();

/**
 * Reads a draft that came from storage.
 *
 * Returns null rather than throwing for anything unusable, and fills every absent field from
 * `emptyDraft()` so the result is always complete-shaped even if the stored row is partial.
 */
export function parseDraft(value: unknown): OnboardingDraft | null {
  const parsed = draftShapeSchema.safeParse(value);

  if (!parsed.success) {
    return null;
  }

  if (parsed.data.version !== undefined && parsed.data.version !== draftVersion) {
    return null;
  }

  const base = emptyDraft();

  return {
    version: draftVersion,
    updatedAt: parsed.data.updatedAt ?? '',
    attribution: { ...base.attribution, ...pickStrings(parsed.data.attribution, base.attribution) },
    store: { ...base.store, ...pickStrings(parsed.data.store, base.store) },
    service: mergeService(base.service, parsed.data.service),
  };
}

function pickStrings<T extends object>(
  value: Record<string, unknown> | undefined,
  shape: T,
): Partial<T> {
  const output: Record<string, unknown> = {};

  if (!value) {
    return output as Partial<T>;
  }

  for (const key of Object.keys(shape)) {
    const candidate = value[key];

    if (typeof candidate === 'string') {
      output[key] = candidate;
    }
  }

  return output as Partial<T>;
}

function mergeService(
  base: ServiceDraft,
  value: Record<string, unknown> | undefined,
): ServiceDraft {
  if (!value) {
    return base;
  }

  return {
    ...base,
    ...pickStrings(value, { deliveryNotes: base.deliveryNotes }),
    schedules: mergeSchedules(base.schedules, value.schedules),
    paymentMethods: readPaymentMethods(value.paymentMethods),
    fulfillmentMethods: readFulfillmentMethods(value.fulfillmentMethods),
    deliveryArea: readDeliveryArea(value.deliveryArea, base.deliveryArea),
  };
}

function mergeSchedules(base: ServiceScheduleDraft[], value: unknown): ServiceScheduleDraft[] {
  if (!Array.isArray(value)) {
    return base;
  }

  return base.map((schedule) => {
    const stored = value.find(
      (candidate) => isRecord(candidate) && candidate.day === schedule.day,
    );

    if (!isRecord(stored)) {
      return schedule;
    }

    return {
      day: schedule.day,
      isOpen: readBoolean(stored.isOpen, schedule.isOpen),
      opensAt: readText(stored.opensAt, schedule.opensAt),
      closesAt: readText(stored.closesAt, schedule.closesAt),
    };
  });
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value !== 'boolean') {
    return fallback;
  }

  return value;
}

function readText(value: unknown, fallback: string): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  return value;
}

function readPaymentMethods(value: unknown): PaymentMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (entry): entry is PaymentMethod => typeof entry === 'string' && paymentMethodValues.includes(entry as PaymentMethod),
  );
}

function readFulfillmentMethods(value: unknown): FulfillmentMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (entry): entry is FulfillmentMethod => typeof entry === 'string' && fulfillmentMethodValues.includes(entry as FulfillmentMethod),
  );
}

function readDeliveryArea(value: unknown, fallback: ServiceDraft['deliveryArea']): ServiceDraft['deliveryArea'] {
  if (!isRecord(value)) {
    return fallback;
  }

  const enabled = readBoolean(value.enabled, fallback.enabled);
  const radiusKm = readOptionalPositiveNumber(value.radiusKm);
  const neighborhoods = readStringList(value.neighborhoods);

  return { enabled, radiusKm, neighborhoods };
}

function readOptionalPositiveNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

function readStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return !Array.isArray(value);
}
